import { Box } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import ScrollableList from 'modules/core/components/ScrollableList'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import { ShiftInputsAttributesByDate } from 'modules/shift_management/hooks/useDefaultAttributesFromRows'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import type DefaultShiftWithParamsForm from 'modules/shift_management/lib/forms/DefaultShiftWithParamsForm'

import List from './List'

type Form = DefaultShiftWithParamsForm
type FormWithSpaces = {
  formsWithSameSpace: Form[]
  formsWithDifferentSpace: Form[]
}

interface Props {
  forms: Form[]
  onToggleShift: (id: string) => void
  defaultAttributes: ShiftInputsAttributesByDate
}

const SavedShiftsList = ({ forms, onToggleShift, defaultAttributes }: Props) => {
  const [{ view }] = useSchedule()
  const { formsWithSameSpace, formsWithDifferentSpace }: FormWithSpaces = useUnsafeMemo(() => {
    const defaultValue = { formsWithSameSpace: [], formsWithDifferentSpace: [] }

    if (view.isByEmployees) return defaultValue

    const preselectedAreas = Object.values(defaultAttributes).flat()
    const { preselectedLocations, preselectedWorkAreas } = preselectedAreas.reduce(
      (acc: { preselectedLocations: Set<string>; preselectedWorkAreas: Set<string> }, s) => {
        const { locationId, workAreaId } = s
        if (locationId && !acc.preselectedLocations.has(locationId)) {
          acc.preselectedLocations.add(locationId)
        }

        if (workAreaId && !acc.preselectedWorkAreas.has(workAreaId)) {
          acc.preselectedWorkAreas.add(workAreaId)
        }
        return acc
      },
      { preselectedLocations: new Set([]), preselectedWorkAreas: new Set([]) }
    )

    return forms.reduce((acc: FormWithSpaces, form) => {
      const { locationId, workAreaId } = form
      preselectedLocations.has(locationId) || preselectedWorkAreas.has(workAreaId)
        ? acc.formsWithSameSpace.push(form)
        : acc.formsWithDifferentSpace.push(form)

      return acc
    }, defaultValue)
  }, [forms, defaultAttributes, view])

  return (
    <ScrollableList size="full">
      {view.isByAreas ? (
        <Box gap="s16">
          {!!formsWithSameSpace.length && (
            <List
              label={i18n.t('shift_management.actions_sidepanel.saved_shifts.same_workplace')}
              forms={formsWithSameSpace}
              onToggleShift={onToggleShift}
            />
          )}
          {!!formsWithDifferentSpace.length && (
            <List
              label={i18n.t('shift_management.actions_sidepanel.saved_shifts.other_workplace')}
              forms={formsWithDifferentSpace}
              onToggleShift={onToggleShift}
            />
          )}
        </Box>
      ) : (
        <List forms={forms} onToggleShift={onToggleShift} />
      )}
    </ScrollableList>
  )
}

export default observer(SavedShiftsList)
